/**
 * auth/LoginScreen.jsx
 *
 * Two states:
 *   mode="signed-out" — branded sign-in page with a Microsoft SSO button.
 *   mode="warming"    — shows the signed-in account + a spinner while the
 *                       API/DB warms up (Azure SQL serverless cold start
 *                       can take up to ~60s after auto-pause).
 *
 * The "redirecting" mode is used while MSAL bounces through login.microsoftonline.com.
 */

import React, { useEffect, useState } from 'react';
import logoMain from '../assets/ae_logo_main.svg';
import useBootStage from '../hooks/useBootStage.js';

const COLD_HINT_AFTER_MS = 10_000;

function MicrosoftLogo({ size = 18 }) {
  // 4-square Microsoft brand mark
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function Spinner() {
  return (
    <span className="login-spinner" role="status" aria-label="Loading">
      <span className="login-spinner-dot" />
      <span className="login-spinner-dot" />
      <span className="login-spinner-dot" />
    </span>
  );
}

function BrandHeader() {
  return (
    <div className="login-brand">
      <img src={logoMain} alt="AE Tracker · Therapie Clinic" className="login-logo-img" />
    </div>
  );
}

export default function LoginScreen({
  mode,                // 'signed-out' | 'warming' | 'redirecting'
  account,             // { name, email, initials } when authenticated
  onSignIn,            // callback for the SSO button
  onCancel,            // callback shown during warming if user wants to bail
  error,               // optional error message
}) {
  // Show a friendlier "cold start" hint if warmup exceeds 10s
  const [showColdHint, setShowColdHint] = useState(false);
  const bootStage = useBootStage();
  useEffect(() => {
    if (mode !== 'warming') { setShowColdHint(false); return; }
    const t = setTimeout(() => setShowColdHint(true), COLD_HINT_AFTER_MS);
    return () => clearTimeout(t);
  }, [mode]);

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-halo" aria-hidden="true"><i /></div>
        <BrandHeader />

        {mode === 'signed-out' && (
          <>
            <div className="login-headline">
              <h1>Sign in to continue</h1>
              <p>Use your Therapie Clinic Microsoft account.</p>
            </div>
            <button type="button" className="login-sso" onClick={onSignIn}>
              <MicrosoftLogo size={18} />
              <span>Sign in with Microsoft</span>
            </button>
            {error && <p className="login-error">{error}</p>}
            <p className="login-foot">
              Access is restricted to authorised staff.
              <br />
              Contact IT Support if you need access.
            </p>
          </>
        )}

        {mode === 'redirecting' && (
          <>
            <div className="login-status">
              <Spinner />
              <div>
                <div className="login-status-title">Redirecting to Microsoft…</div>
                <div className="login-status-sub">Complete sign-in in the next window.</div>
              </div>
            </div>
          </>
        )}

        {mode === 'unauthorized' && (
          <>
            <div className="login-account">
              <div className="login-avatar">{account?.initials || '·'}</div>
              <div className="login-account-meta">
                <div className="login-account-name">{account?.name || 'Signed in'}</div>
                <div className="login-account-email">{account?.email || ''}</div>
              </div>
            </div>
            <div className="login-headline">
              <h1>Access denied</h1>
              <p>
                You don't have permission to access this application.
                <br />
                Contact IT Support to request access.
              </p>
            </div>
            <button type="button" className="login-sso" onClick={onCancel}>
              Sign out
            </button>
          </>
        )}

        {mode === 'warming' && (
          <>
            <div className="login-account">
              <div className="login-avatar">{account?.initials || '·'}</div>
              <div className="login-account-meta">
                <div className="login-account-name">{account?.name || 'Signed in'}</div>
                <div className="login-account-email">{account?.email || ''}</div>
              </div>
              <span className="login-check" aria-label="Signed in">✓</span>
            </div>

            <div className="login-status">
              <Spinner />
              <div>
                <div className="login-status-title">Connecting to AE Tracker…</div>
                <div className="login-status-sub">
                  {showColdHint
                    ? 'Starting the app — first load after idle can take a few seconds.'
                    : 'Loading your workspace.'}
                </div>
                {bootStage.detail && (
                  <div className="login-warmup-detail">{bootStage.detail}</div>
                )}
              </div>
            </div>

            {onCancel && (
              <button type="button" className="login-link" onClick={onCancel}>
                Sign out
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
